import React, { useState, useMemo } from 'react'
import { object, bool, string } from 'prop-types'

import { branch } from 'baobab-react/higher-order'
import storage from 'utils/storage'
import snakeCaseKeys from 'snakecase-keys'
import API from 'utils/API'
import csrfToken from 'utils/csrfToken'
import AccessToken from 'utils/AccessToken'

import BaseTemplate from 'components/templates/BaseTemplate'

import Container from 'components/atoms/Container'

import Typography from 'components/atoms/Typography'
import Markdown from 'components/atoms/MarkdownContent'

import Form from 'components/organisms/Form'
import FormGroup from 'components/molecules/FormGroup'
import InputField from 'components/atoms/InputField'
import DatePicker from 'components/atoms/DatePicker'
import Select from 'react-select'
import AsyncCreatableSelect from 'react-select/async-creatable'
import AsyncSelect from 'react-select/async'
import { Row, Col, Checkbox } from 'components/qstrap'
import Button from 'components/atoms/Button'
import Loader from 'components/utils/Loader'

import participantsData from './participants.js'

const INSTRUCTION = `
  Silakan mengisi formulir pendaftaran di bawah ini dengan lengkap.

  Kamu hanya bisa mendaftar satu kali. Pastikan kamu sudah membaca seluruh persyaratan [di sini](https://campus.quipper.com/scholarships/award).

  Informasi mengenai semua proses seleksi hingga pengumuman akan dikirimkan melalui email dan/atau no telepon. Oleh karena itu pastikan kamu mencantumkan email dan no telepon yang aktif.

  Peserta dapat memilih maksimal dua (2) kampus.

  Pendaftaran: 17 Februari - 17 April 2020

  Pelaksanaan Ujian Online: 20 April - 1 Mei 2020

  Seleksi Lanjutan: April - Mei 2020

  Terima kasih
`
const TUITION_OPTS = [
  {
    value: 'yes',
    label: 'Ya',
  },
  {
    value: 'maybe',
    label: 'Mungkin',
  },
  {
    value: 'no',
    label: 'Tidak',
  },
]
const INCOME_RANGES = [
  {
    value: '< Rp 5,000,000',
    label: '< Rp 5,000,000',
  },
  {
    value: 'Rp 5,000,000 - Rp 10,000,000',
    label: 'Rp 5,000,000 - Rp 10,000,000',
  },
  {
    value: 'Rp 10,000,000 - Rp 20,000,000',
    label: 'Rp 10,000,000 - Rp 20,000,000',
  },
  {
    value: 'Rp 20,000,000 - Rp 30,000,000',
    label: 'Rp 20,000,000 - Rp 30,000,000',
  },
  {
    value: '> Rp 30,000,000',
    label: '> Rp 30,000,000',
  },
]

const ScholarshipAward = ({ registered, quotaFulfilledCampus, userProfile }) => {
  const REQ_OPTS = {
    headers: {
      'X-CSRF-Token': csrfToken(),
      Authorization: `Token ${AccessToken.get()}`,
    },
  }
  const THANKS_GREETING = `
  Terima kasih telah mendaftar Quipper Scholarship Award 2020.

  Tunggu instruksi selanjutnya melalui email dan no telepon.

  Jangan lupa follow instagram [@quippercampus](https://instagram.com/quippercampus) untuk informasi terbaru!
  `

  const CLOSED_GREETING = `
  Pendaftaran Quipper Scholarship Award 2020 telah ditutup.

  Silakan cek email kamu secara berkala untuk mendapatkan instruksi pengerjaan tes.

  Jika ada pertanyaan, jangan ragu untuk menyampaikan via instagram [@quippercampus](https://instagram.com/quippercampus).
  `
  const OPTION_PLACEHOLDER = {
    value: '',
    label: 'Masukkan 1 karakter untuk mulai mencari',
    isDisabled: true,
  }

  const getClosedStatus = () => new Date().getTime() > new Date(Date.UTC(2020, 3, 17, 16, 59, 59))

  const [isClosed, setIsClosed] = useState(getClosedStatus())

  const [formState, setFormState] = useState({
    fullName: `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim(),
    schoolName: userProfile.school_name || '',
    schoolId: userProfile.school_id || '',
    email: userProfile.email || '',
    phone: userProfile.phone_number || '',
    address: '',
    province: userProfile.province || '',
    city: userProfile.city || '',
    placeOfBirth: '',
    dateOfBirth: '',
    parentName: '',
    parentPhone: userProfile.parent_phone_number || '',
    parentIncome: '',
    organizationalExp: '',
    achievements: '',
    installmentOpt: '', /* monthly: yes, no, maybe */
    campusOption1Id: '',
    campusOption2Id: '',
    infoSources: [],
    termsConditionAcceptance: false,
  })
  const [loading, setLoading] = useState(false)

  const unavailableCampus = JSON.parse(quotaFulfilledCampus).map(campus => campus.id)
  const selectedProvince = formState.province ?
    { label: formState.province, value: formState.province } :
    OPTION_PLACEHOLDER
  const selectedCity = formState.city ?
    { label: formState.city, value: formState.city } :
    OPTION_PLACEHOLDER
  const selectedSchool = formState.schoolName ?
    { label: formState.schoolName, value: formState.schoolId } :
    OPTION_PLACEHOLDER

  const createValOpts = fieldCheck => participantsData.map((participant) => {
    const label = (unavailableCampus.indexOf(participant.id) >= 0) ?
      `${participant.label} (kuota pendaftar habis)` :
      participant.label
    const isDisabled = (fieldCheck && formState[fieldCheck] === participant.id) ||
      (unavailableCampus.indexOf(participant.id) >= 0)

    return ({
      ...participant,
      value: participant.id,
      label,
      isDisabled,
    })
  })
  const opts1 = useMemo(() => createValOpts('campusOption2Id'), [formState.campusOption2Id])
  const opts2 = useMemo(() => createValOpts('campusOption1Id'), [formState.campusOption1Id])


  const handleInputChange = (inputData) => {
    const { name: key, value } = inputData

    setFormState({
      ...formState,
      [key]: value,
    })
  }

  const handleInfoSource = (checked, source) => {
    const prevSources = formState.infoSources
    const newSources = checked ? [...prevSources, source] :
      prevSources.filter(prevSource => prevSource !== source)

    setFormState({
      ...formState,
      infoSources: newSources,
    })
  }

  const provinceOptions = async (inputVal) => {
    const getProvincesPath = `${API.endPoints().schools.provinces}?country_code=ID`
    const response = await API.get(getProvincesPath)
    return response.data
      .filter(p => p.toLowerCase().includes(inputVal.toLowerCase()))
      .map(prov => ({ label: prov, value: prov }))
  }

  const cityOptions = async (inputVal) => {
    const getCitiesPath = `${API.endPoints().schools.cities}?country_code=ID&province=${formState.province}`
    const response = await API.get(getCitiesPath)
    return response.data
      .filter(c => c.toLowerCase().includes(inputVal.toLowerCase()))
      .map(city => ({ label: city, value: city }))
  }

  const schoolOptions = async (inputVal) => {
    const getSchoolsPath = `${API.endPoints().schools.select}?country_code=ID&province=${formState.province}&city=${formState.city}&limit=50&verified_only=true&query=${inputVal}`
    const response = await API.get(getSchoolsPath)
    return response.data.results
      .filter(s => s.text.toLowerCase().includes(inputVal.toLowerCase()))
      .map(school => ({ label: school.text, value: school.id }))
  }

  const updateUserData = async () => {
    const firstName = formState.fullName.substring(0, formState.fullName.lastIndexOf(' '))
    const lastName = formState.fullName.split(' ').pop()
    const payload = {
      first_name: firstName,
      last_name: lastName,
      email: formState.email,
      phone: formState.phone,
    }
    return API.post(API.endPoints().profile.updateApi, payload, REQ_OPTS)
  }

  const updateVpMeta = async () => {
    const payload = {
      phone_number: formState.phone,
      parent_phone_number: formState.parentPhone,
      existing_user: true,
      email: formState.email,
      name: formState.fullName,
      username: userProfile.username,
      province: formState.province,
      city: formState.city,
      school_name: formState.schoolName,
      school_id: formState.schoolId,
    }
    return API.post(API.endPoints().auth.vp_meta, payload, REQ_OPTS)
  }

  const submitForm = async () => {
    if (!(getClosedStatus())) {
      setLoading(true)
      try {
        if (userProfile.first_name) {
          await updateUserData()
          await updateVpMeta()
        }
        await API.post(API.endPoints().qsaForm,
          {
            qsa_application: {
              ...snakeCaseKeys(formState),
            },
          },
          REQ_OPTS
        )
        setLoading(false)
        storage.set(['serverProps', 'registered'], true)
      } catch (error) {
        // eslint-disable-next-line no-alert
        alert('Oops! Terjadi kesalahan, silakan coba beberapa saat lagi!')
      }
    } else {
      setIsClosed(true)
    }
  }

  return (
    <BaseTemplate>
      <div className="qsa-form">
        <Container>
          <Row>
            <Col md="8" className="col-md-offset-2">
              {
                isClosed &&
                  <div className="qsa-form__thanks">
                    <Typography component="h1" variant="title">
                      Terima kasih!
                    </Typography>
                    <Markdown content={CLOSED_GREETING} />
                  </div>
              }
              {
                (!isClosed && registered && !loading) &&
                  <div className="qsa-form__thanks">
                    <Typography component="h1" variant="title">
                      Hi {formState.fullName}!
                    </Typography>
                    <Markdown content={THANKS_GREETING} />
                  </div>
              }
              {
                (!isClosed && !registered && !loading) &&
                  <>
                    <Typography component="h1" variant="title">
                      Formulir Pendaftaran - Quipper Scholarship Award 2020
                    </Typography>
                    <div className="qsa-form__desc">
                      <Markdown content={INSTRUCTION} />
                      <p style={{ color: '#ff0000' }}>* Required</p>
                      <br/>
                    </div>

                    <Form onSubmit={submitForm}>
                      {/* ===== full name ===== */}
                      <FormGroup
                        id="fullName"
                        labelText="Nama Lengkap (Sesuai Rapor)"
                        required
                      >
                        <InputField
                          type="text"
                          name="fullName"
                          placeholder="Nama Lengkap (Sesuai Rapor)"
                          validations={[
                            {
                              rule: 'required',
                              invalidText: 'Tolong masukkan nama lengkap kamu',
                            },
                          ]}
                          handleChange={handleInputChange}
                          value={formState.fullName}
                          isRequired
                        />
                      </FormGroup>
                      {/* ===== school province ===== */}
                      <FormGroup
                        id="province"
                        labelText="Provinsi Sekolah"
                        required
                      >
                        <AsyncSelect
                          cacheOptions
                          defaultOptions={[selectedProvince]}
                          loadOptions={provinceOptions}
                          name="province"
                          placeholder="Pilih nama provinsi"
                          value={formState.province ? selectedProvince : null}
                          onChange={
                            selected => handleInputChange({
                              name: 'province',
                              value: selected.value,
                            })
                          }
                          isSearchable
                        />
                      </FormGroup>
                      {/* ===== school city ===== */}
                      <FormGroup
                        id="city"
                        labelText="Kota Sekolah"
                        required
                      >
                        <AsyncSelect
                          defaultOptions={[selectedCity]}
                          loadOptions={cityOptions}
                          name="city"
                          isDisabled={!formState.province}
                          placeholder="Pilih nama kota"
                          value={formState.city ? selectedCity : null}
                          onChange={
                            selected => handleInputChange({
                              name: 'city',
                              value: selected.value,
                            })
                          }
                          isSearchable
                        />
                      </FormGroup>
                      {/* ===== school name ===== */}
                      <FormGroup
                        id="schoolName"
                        labelText="Asal Sekolah"
                        required
                      >
                        <AsyncCreatableSelect
                          loadOptions={schoolOptions}
                          name="school"
                          defaultOptions={[selectedSchool]}
                          isDisabled={!formState.province && !formState.city}
                          placeholder="Pilih nama sekolah"
                          value={(formState.schoolName || formState.schoolId) ? selectedSchool : null}
                          onChange={
                            (selected) => {
                              setFormState({
                                ...formState,
                                schoolId: selected.value,
                                schoolName: selected.label,
                              })
                            }
                          }
                          isSearchable
                        />
                      </FormGroup>

                      {/* ===== email ===== */}
                      <FormGroup
                        id="email"
                        labelText="Email Aktif"
                        required
                      >
                        <InputField
                          type="email"
                          name="email"
                          placeholder="Email Aktif"
                          validations={[
                            {
                              rule: 'required',
                              invalidText: 'Tolong masukkan email kamu yang masih aktif',
                            },
                          ]}
                          handleChange={handleInputChange}
                          value={formState.email}
                          isRequired
                        />
                      </FormGroup>

                      {/* ===== phone ===== */}
                      <FormGroup
                        id="phone"
                        labelText="No. HP (WA Aktif)"
                        required
                      >
                        <InputField
                          type="tel"
                          name="phone"
                          placeholder="No. HP (WA Aktif)"
                          validations={[
                            {
                              rule: 'required',
                              invalidText: 'Tolong masukkan No. HP dengan WA yang aktif',
                            },
                            {
                              rule: 'phone_number',
                              invalidText: 'Pastikan No. HP yang kamu masukkan valid',
                            },
                          ]}
                          handleChange={handleInputChange}
                          value={formState.phone}
                          isRequired
                        />
                      </FormGroup>

                      {/* ===== address ===== */}
                      <FormGroup
                        id="address"
                        labelText="Alamat Rumah Lengkap"
                        required
                      >
                        <InputField
                          type="text"
                          name="address"
                          placeholder="Alamat Rumah Lengkap"
                          validations={[
                            {
                              rule: 'required',
                              invalidText: 'Tolong masukkan alamat lengkap rumah kamu',
                            },
                          ]}
                          handleChange={handleInputChange}
                          value={formState.address}
                          isRequired
                          isTextarea
                        />
                      </FormGroup>

                      <Row>
                        <Col md="6">
                          {/* ===== place of birth ===== */}
                          <FormGroup
                            id="placeOfBirth"
                            labelText="Tempat Lahir"
                            required
                          >
                            <InputField
                              type="text"
                              name="placeOfBirth"
                              placeholder="Tempat Lahir"
                              validations={[
                                {
                                  rule: 'required',
                                  invalidText: 'Tolong masukkan kota tempat lahir kamu',
                                },
                              ]}
                              handleChange={handleInputChange}
                              value={formState.placeOfBirth}
                              isRequired
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6" className="custom-field">
                          {/* ===== date of birth ===== */}
                          <FormGroup
                            id="dateOfBirth"
                            labelText="Tanggal Lahir"
                            required
                          >
                            <DatePicker
                              fieldName="dateOfBirth"
                              errorMsg="Tolong masukkan tanggal lahir kamu"
                              onChange={handleInputChange}
                            />
                          </FormGroup>
                          <input className="validation-field" type="text" value={formState.dateOfBirth} required />
                        </Col>
                      </Row>

                      {/* ===== parent name ===== */}
                      <FormGroup
                        id="parentName"
                        labelText="Nama Orang Tua (Wali)"
                        required
                      >
                        <InputField
                          type="text"
                          name="parentName"
                          placeholder="Nama Orang Tua (Wali)"
                          validations={[
                            {
                              rule: 'required',
                              invalidText: 'Tolong masukkan kota nama orang tua (wali) kamu',
                            },
                          ]}
                          handleChange={handleInputChange}
                          value={formState.parentName}
                          isRequired
                        />
                      </FormGroup>

                      {/* ===== parent's phone num ===== */}
                      <FormGroup
                        id="parentPhone"
                        labelText="No. HP Orang Tua (Wali)"
                        required
                      >
                        <InputField
                          type="tel"
                          name="parentPhone"
                          placeholder="No. HP Orang Tua (Wali)"
                          validations={[
                            {
                              rule: 'required',
                              invalidText: 'Tolong masukkan No. HP orang tua kamu',
                            },
                            {
                              rule: 'phone_number',
                              invalidText: 'Pastikan No. HP orang tua yang kamu masukkan valid',
                            },
                          ]}
                          handleChange={handleInputChange}
                          value={formState.parentPhone}
                          isRequired
                        />
                      </FormGroup>

                      {/* ===== parentIncome ===== */}
                      <FormGroup
                        id="parentIncome"
                        labelText="Total Penghasilan Orang Tua/Wali per Bulan"
                        required
                      >
                        <Select
                          name="parentIncome"
                          options={INCOME_RANGES}
                          onChange={
                            selected => handleInputChange({
                              name: 'parentIncome',
                              value: selected.value,
                            })
                          }
                          isSearchable
                        />
                      </FormGroup>
                      <input className="validation-field" type="text" value={formState.parentIncome} required />

                      {/* ===== organizational experiences ===== */}
                      <FormGroup
                        id="organizationalExp"
                        labelText="Pengalaman Organisasi"
                      >
                        <InputField
                          type="text"
                          name="organizationalExp"
                          placeholder="Pengalaman Organisasi (Tidak Wajib, Maksimal 3 Jika Ada)"
                          handleChange={handleInputChange}
                          value={formState.organizationalExp}
                          isTextarea
                        />
                      </FormGroup>

                      {/* ===== achievements ===== */}
                      <FormGroup
                        id="achievements"
                        labelText="Daftar Prestasi (3 Terbaik)"
                      >
                        <InputField
                          type="text"
                          name="achievements"
                          placeholder="Daftar Prestasi (Tidak Wajib, Maksimal 3 Jika Ada)"
                          handleChange={handleInputChange}
                          value={formState.achievements}
                          isTextarea
                        />
                      </FormGroup>

                      {/* ===== installmentOpt ===== */}
                      <FormGroup
                        id="installmentOpt"
                        labelText="Apakah kamu tertarik jika biaya kuliahmu dapat dicicil per bulan?"
                        required
                      >
                        <Select
                          name="installmentOpt"
                          options={TUITION_OPTS}
                          onChange={
                            selected => handleInputChange({
                              name: 'installmentOpt',
                              value: selected.value,
                            })
                          }
                          isSearchable
                        />
                      </FormGroup>
                      <input className="validation-field" type="text" value={formState.installmentOpt} required />

                      {/* ===== first campus options ===== */}
                      <FormGroup
                        id="campusOption1Id"
                        labelText="Pilihan Kampus 1"
                        required
                      >
                        <Select
                          name="campusOption1Id"
                          options={opts1}
                          onChange={
                            selected => handleInputChange({
                              name: 'campusOption1Id',
                              value: selected.value,
                            })
                          }
                          isSearchable
                        />
                      </FormGroup>
                      <input className="validation-field" type="text" value={formState.campusOption1Id} required />

                      {/* ===== second campus options ===== */}
                      <FormGroup
                        id="campusOption2Id"
                        labelText="Pilihan Kampus 2"
                      >
                        <Select
                          name="campusOption2Id"
                          options={opts2}
                          onChange={
                            selected => handleInputChange({
                              name: 'campusOption2Id',
                              value: selected ? selected.value : '',
                            })
                          }
                          isDisabled={!(formState.campusOption1Id || formState.campusOption2Id)}
                          isSearchable
                          isClearable
                        />
                      </FormGroup>

                      {/* ===== info source ===== */}
                      <FormGroup
                        id="infoSources"
                        labelText="Darimana kamu mengetahui Quipper Scholarship Award 2020 ini?"
                        required
                      >
                        <div className="info-sources-option">
                          <Checkbox
                            checked={formState.infoSources.indexOf('blog') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'blog')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>Blog</span>
                          </Checkbox>
                          <Checkbox
                            checked={formState.infoSources.indexOf('email') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'email')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>Email</span>
                          </Checkbox>
                          <Checkbox
                            checked={formState.infoSources.indexOf('google') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'google')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>Google</span>
                          </Checkbox>
                          <Checkbox
                            checked={formState.infoSources.indexOf('internet_ads') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'internet_ads')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>Iklan di Internet</span>
                          </Checkbox>
                          <Checkbox
                            checked={formState.infoSources.indexOf('socmed') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'socmed')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>Media Sosial</span>
                          </Checkbox>
                          <Checkbox
                            checked={formState.infoSources.indexOf('qlearn') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'qlearn')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>QLearn</span>
                          </Checkbox>
                          <Checkbox
                            checked={formState.infoSources.indexOf('school') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'school')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>Sekolah/Guru</span>
                          </Checkbox>
                          <Checkbox
                            checked={formState.infoSources.indexOf('friend') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'friend')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>Teman</span>
                          </Checkbox>
                          <Checkbox
                            checked={formState.infoSources.indexOf('website') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'website')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>Website</span>
                          </Checkbox>
                          <Checkbox
                            checked={formState.infoSources.indexOf('wa_broadcast') >= 0}
                            onChange={
                              e => handleInfoSource(e.target.checked, 'wa_broadcast')
                            }
                          >
                            <span style={{ fontSize: '16px', marginLeft: '8px' }}>WhatsApp Broadcast</span>
                          </Checkbox>
                        </div>
                      </FormGroup>
                      <input className="validation-field" type="text" value={formState.infoSources} required />

                      {/* ===== terms condition acceptance ===== */}
                      <FormGroup
                        id="termsConditionAcceptance"
                        labelText="Saya telah memahami setiap persyaratan yang akan dilalui dalam proses seleksi dan bersedia untuk dihubungi oleh kampus yang saya pilih."
                        required
                      >
                        <Checkbox
                          name="termsConditionAcceptance"
                          checked={formState.termsConditionAcceptance}
                          onChange={
                            e => setFormState({ ...formState, termsConditionAcceptance: e.target.checked })
                          }
                        >
                          <span style={{ fontSize: '16px', marginLeft: '8px' }}>Ya</span>
                        </Checkbox>
                      </FormGroup>
                      <Button
                        className={!formState.termsConditionAcceptance ? 'btn btn-disabled' : ''}
                        component="button"
                        type="submit"
                        variant="default"
                        disabled={!formState.termsConditionAcceptance}
                      >
                        Submit
                      </Button>
                    </Form>
                  </>
              }
              {loading && <Loader />}
            </Col>
          </Row>
        </Container>
      </div>
      <style jsx>
        {`
          .qsa-form {
            background-color: #fff;
            padding: 40px 0 80px;
            &__thanks {
              text-align: center;
              padding: 80px 0 120px;
            }
            .validation-field {
              height: 0;
              opacity: 0;
              padding: 0;
              position: absolute;
              margin-top: -16px;
            }
            :global(input:checked + .checkbox-indicator) {
              background-color: #1e90ff;
            }
            :global(.checkbox-indicator) {
              background-color: #fff;
            }
            :global(.text-variant-title) {
              @media screen and (max-width: 767px) {
                font-size: 24px;
              }
            }
            :global(.btn-disabled) {
              background-color: #d3dce6;
              border: #d3dce6;
              cursor: not-allowed;
            }
            span.label {
              font-size: 16px;
              margin-left: 8px;
              color: #485c77;
            }
          }
        `}
      </style>
    </BaseTemplate>
  )
}

ScholarshipAward.propTypes = {
  registered: bool,
  quotaFulfilledCampus: string,
  userProfile: object,
}

export default branch({
  registered: ['serverProps', 'registered'],
  quotaFulfilledCampus: ['serverProps', 'quotaFulfilledCampus'],
  userProfile: ['serverProps', 'userProfile'],
}, ScholarshipAward)
